/**
 * NpcEncounterServiceModule — A8r (2026-06-04).
 *
 * Wraps the briefing-room backend's NPC encounter registry routes.
 * Slice A scope: read the encountered-NPCs list + record new encounters.
 *
 * Routes (per briefing_room_backend/routes/npcs.py):
 *   GET    /api/modules/briefing-room/campaigns/{cid}/npcs
 *          → NpcRegistry (encounters[])
 *   POST   /api/modules/briefing-room/campaigns/{cid}/npcs
 *          → EncounterEntry (200 new OR existing — idempotent on
 *            (npc_id, scenario_id))
 *
 * Auth: Bearer JWT from localStorage via getAuthHeaders() — mirrors
 * HeroPointServiceModule pattern.
 */
import { getAuthHeaders } from './authHeaders';

export interface EncounterEntry {
  id: string;
  npc_id: string;
  scenario_id: string;
  paragraph: number;
  occurred_at: string;
}

export interface NpcRegistry {
  encounters: EncounterEntry[];
}

const BASE_PATH = '/api/modules/briefing-room/campaigns';

export class NpcEncounterServiceModule {
  constructor(
    private readonly fetchFn: typeof fetch = (input, init) => fetch(input, init),
  ) {}

  private url(campaignId: string): string {
    return `${BASE_PATH}/${encodeURIComponent(campaignId)}/npcs`;
  }

  async getRegistry(campaignId: string): Promise<NpcRegistry> {
    const resp = await this.fetchFn(this.url(campaignId), {
      method: 'GET',
      headers: { ...getAuthHeaders() },
    });
    if (!resp.ok) {
      throw new Error(
        `NpcEncounterServiceModule: getRegistry HTTP ${resp.status}`,
      );
    }
    return (await resp.json()) as NpcRegistry;
  }

  async recordEncounter(
    campaignId: string,
    npcId: string,
    scenarioId: string,
    paragraph: number,
  ): Promise<EncounterEntry> {
    const resp = await this.fetchFn(this.url(campaignId), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify({
        npc_id: npcId,
        scenario_id: scenarioId,
        paragraph,
      }),
    });
    if (!resp.ok) {
      throw new Error(
        `NpcEncounterServiceModule: recordEncounter HTTP ${resp.status}`,
      );
    }
    return (await resp.json()) as EncounterEntry;
  }
}
